import React, { useEffect, useState } from "react";

import endpoint from "../../../config/endpoints";
import { apiGet } from "../../../utils/api_service";

export default function SalesTeamOverview() {
  const [data, setData] = useState({
    total: 284,
    active: 251,
    inactive: 33,
    present: 218,
    onLeave: 19,
    newJoiners: 12,
    resigned: 5,
    notReporting: 14,
  });

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                                   API                                      */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => {
      apiGet({
        url: endpoint.salesExecutive + "/get-sales-person-by-areas-optimize",
      })
        .then(({ data, success }) => {
          setLoading(false);

          console.log(data);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    }, 1800);

    return () => clearTimeout(timer);
  }, []);

  /* -------------------------------------------------------------------------- */
  /*                                  Loader                                    */
  /* -------------------------------------------------------------------------- */

  if (loading) {
    return <SalesTeamOverviewSkeleton />;
  }

  return (
    <>
      {/* Shimmer Animation */}

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }

            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>

      {/* Header */}

      <SectionHeader icon="👥" title="Sales Team Overview" />

      {/* Cards */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard
          label="Total Employees"
          value={data.total}
          sub="All sales roles"
          accent="gray"
          icon="🏢"
        />

        <StatCard
          label="Active"
          value={data.active}
          sub="88.4% of total"
          accent="green"
          icon="✅"
        />

        <StatCard
          label="Inactive"
          value={data.inactive}
          sub="11.6% of total"
          accent="amber"
          icon="⏸️"
        />

        <StatCard
          label="Present Today"
          value={data.present}
          sub="+3 vs yesterday"
          accent="green"
          icon="🟢"
        />

        <StatCard
          label="On Leave"
          value={data.onLeave}
          sub="Approved leaves"
          accent="blue"
          icon="🏖️"
        />

        <StatCard
          label="New Joiners"
          value={data.newJoiners}
          sub="This month"
          accent="blue"
          icon="🆕"
        />

        <StatCard
          label="Resigned"
          value={data.resigned}
          sub="This month"
          accent="red"
          icon="👋"
        />

        <StatCard
          label="Not Reporting"
          value={data.notReporting}
          sub="Action needed"
          accent="amber"
          icon="⚠️"
        />
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Stat Card                                  */
/* -------------------------------------------------------------------------- */

function StatCard({ label, value, sub, accent, icon }) {
  const accents = {
    green: {
      bg: "#ECFDF5",
      text: "#059669",
      border: "#A7F3D0",
    },

    red: {
      bg: "#FEF2F2",
      text: "#DC2626",
      border: "#FECACA",
    },

    amber: {
      bg: "#FFFBEB",
      text: "#D97706",
      border: "#FDE68A",
    },

    blue: {
      bg: "#EFF6FF",
      text: "#2563EB",
      border: "#BFDBFE",
    },

    gray: {
      bg: "#F8FAFC",
      text: "#64748B",
      border: "#E2E8F0",
    },
  };

  const c = accents[accent] || accents.gray;

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        transition: "transform 0.15s, box-shadow 0.15s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";

        e.currentTarget.style.boxShadow = `0 8px 24px ${c.border}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";

        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#94A3B8",
          }}
        >
          {label}
        </span>

        <span
          style={{
            fontSize: 18,
            color: c.text,
          }}
        >
          {icon}
        </span>
      </div>

      {/* Value */}

      <span
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: c.text,
          lineHeight: 1.1,
        }}
      >
        <AnimatedNumber value={value} />
      </span>

      {/* Sub */}

      {sub && (
        <span
          style={{
            fontSize: 11,
            color: "#94A3B8",
            fontWeight: 500,
          }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Animated Number                               */
/* -------------------------------------------------------------------------- */

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start = 0;

    const end = value;

    const duration = 900;

    const step = end / (duration / 16);

    const timer = setInterval(() => {
      start += step;

      if (start >= end) {
        setDisplay(end);

        clearInterval(timer);
      } else {
        setDisplay(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return <>{display.toLocaleString()}</>;
}

/* -------------------------------------------------------------------------- */
/*                               Section Header                               */
/* -------------------------------------------------------------------------- */

function SectionHeader({ icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        marginTop: 32,
        paddingBottom: 10,
        borderBottom: "1.5px solid #F1F5F9",
      }}
    >
      <span
        style={{
          fontSize: 20,
        }}
      >
        {icon}
      </span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#475569",
        }}
      >
        {title}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Skeleton Components                             */
/* -------------------------------------------------------------------------- */

function SalesTeamOverviewSkeleton() {
  return (
    <>
      {/* Shimmer */}

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }

            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>

      {/* Header */}

      <SectionHeader icon="👥" title="Sales Team Overview" />

      {/* Skeleton Cards */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </>
  );
}

function SkeletonCard() {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 14,
        padding: "18px 20px",
      }}
    >
      {/* Header */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <SkeletonLine width="50%" height={10} />

        <SkeletonCircle size={20} />
      </div>

      {/* Value */}

      <SkeletonLine width="40%" height={30} />

      {/* Footer */}

      <div style={{ marginTop: 18 }}>
        <SkeletonLine width="65%" height={10} />
      </div>
    </div>
  );
}

function SkeletonLine({ width, height }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 999,
        background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}

function SkeletonCircle({ size }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}
